import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { useProcurifyIntl } from '@webapp/hooks'
import { type POPDFCustomFieldDisplayRow } from '@webapp/modules/purchasing/business-logic/use-cases/getPOPDFCustomFieldDisplay'
import { POPDFCustomFieldToggleTable } from '@webapp/modules/purchasing/features/list-po-pdf-custom-field-display/views/POPDFCustomFieldToggleTable'
import messages from './messages'
import { usePOPDFCustomFieldDisplayPresenter } from './usePOPDFCustomFieldDisplayPresenter'

export interface IPOPDFCustomFieldDisplayPresenterProps {
  onToggle: (
    field: CustomField,
    hidden: boolean,
    currentRows: {
      po: POPDFCustomFieldDisplayRow[]
      orderItem: POPDFCustomFieldDisplayRow[]
    }
  ) => void
  /**
   * Transient optimistic flips owned by the Feature Set (field id →
   * displayInPDF). Applied over the query rows so a toggle reflects
   * immediately and can be reverted on save failure (BHV-045/046).
   */
  displayOverrides?: Record<number, boolean>
  isSaving?: boolean
}

export const POPDFCustomFieldDisplayPresenter = ({
  onToggle,
  displayOverrides,
  isSaving = false,
}: IPOPDFCustomFieldDisplayPresenterProps) => {
  const intl = useProcurifyIntl()

  const {
    data: { poRows, orderItemRows },
  } = usePOPDFCustomFieldDisplayPresenter()

  const applyOverrides = (
    rows: POPDFCustomFieldDisplayRow[]
  ): POPDFCustomFieldDisplayRow[] =>
    rows.map((row) =>
      displayOverrides && row.field.id in displayOverrides
        ? { ...row, displayInPDF: displayOverrides[row.field.id] }
        : row
    )

  const displayedPoRows = applyOverrides(poRows)
  const displayedOrderItemRows = applyOverrides(orderItemRows)

  const handleToggle = (field: CustomField, hidden: boolean) => {
    onToggle(field, hidden, {
      po: displayedPoRows,
      orderItem: displayedOrderItemRows,
    })
  }

  return (
    <>
      {displayedPoRows.length > 0 && (
        <POPDFCustomFieldToggleTable
          title={intl.formatMessage(messages.poTableTitle)}
          rows={displayedPoRows}
          onToggle={handleToggle}
          isSaving={isSaving}
        />
      )}
      {displayedOrderItemRows.length > 0 && (
        <POPDFCustomFieldToggleTable
          title={intl.formatMessage(messages.orderItemTableTitle)}
          rows={displayedOrderItemRows}
          onToggle={handleToggle}
          isSaving={isSaving}
        />
      )}
    </>
  )
}

POPDFCustomFieldDisplayPresenter.displayName =
  'POPDFCustomFieldDisplayPresenter'
